import { mapGetters } from 'vuex'
import Utils from '@/utils'

const computed = {
  ...mapGetters({
    chamber: 'calculator/chamber',
    mode: 'calculator/mode'
  }),
  totalSpend () {
    let dem = 0
    let gop = 0
    this.expenditures.forEach((item) => {
      dem += item.demExpenditures
      gop += item.gopExpenditures
    })
    return { dem, gop }
  },
  maxSpend () {
    return this.initialMaxSpend[this.chamber]
  },
  fundsRemaining () {
    return {
      dem: Math.round((this.maxSpend.dem - this.totalSpend.dem)),
      gop: Math.round((this.maxSpend.gop - this.totalSpend.gop))
    }
  },
  favored () {
    if (this.expenditures.length > 0) {
      const gopNewFavored = this.expenditures.filter(item => item.finalProbability >= 0.5)
      const gopOriginalFavored = this.expenditures.filter(item => item.originalProbability >= 0.5)
      const demNewFavored = this.expenditures.filter(item => item.finalProbability < 0.5)
      const demOriginalFavored = this.expenditures.filter(item => item.originalProbability < 0.5)
      return {
        gop: {
          seats: this.chamber === 'house' ? gopNewFavored.length : gopNewFavored.length + 42,
          change: gopNewFavored.length - gopOriginalFavored.length,
          negative: (gopNewFavored.length - gopOriginalFavored.length) < 0
        },
        dem: {
          seats: this.chamber === 'house' ? demNewFavored.length : demNewFavored.length + 23,
          change: demNewFavored.length - demOriginalFavored.length,
          negative: (demNewFavored.length - demOriginalFavored.length) < 0
        }
      }
    }
  },
  expected () {
    if (this.expenditures.length > 0) {
      const newExpectedSeats = Math.round(this.expenditures.map(item => item.finalProbability).reduce((a, b) => a + b))
      const originalExpectedSeats = Math.round(this.expenditures.map(item => item.originalProbability).reduce((a, b) => a + b))
      const totalSeats = this.chamber === 'house' ? 435 : 35
      return {
        gop: {
          seats: this.chamber === 'house' ? newExpectedSeats : newExpectedSeats + 42,
          change: newExpectedSeats - originalExpectedSeats,
          negative: (newExpectedSeats - originalExpectedSeats) < 0,
          original: originalExpectedSeats
        },
        dem: {
          seats: this.chamber === 'house' ? (totalSeats - newExpectedSeats) : (totalSeats - newExpectedSeats) + 23,
          change: (totalSeats - newExpectedSeats) - (totalSeats - originalExpectedSeats),
          negative: ((totalSeats - newExpectedSeats) - (totalSeats - originalExpectedSeats)) < 0,
          original: (totalSeats - originalExpectedSeats)
        }
      }
    }
  }
}

const methods = {
  getBaseProbability (item) {
    if (item.gopWinProb === 0) {
      return 0
    } else if (item.gopWinProb === 1) {
      return 1
    } else {
      const demFactor = this.chamber === 'house' ? -0.0000005597931 : -0.00000005038829
      const gopFactor = this.chamber === 'house' ? 0.0000006411317 : 0.00000006967787
      const dem = demFactor * item.demExpenditures
      const gop = gopFactor * item.gopExpenditures
      return 1 / (1 + Math.exp(-1 * (item.intercept + dem + gop)))
    }
  },
  getFinalProbability (item, adjustedBaseProbability) {
    if (item.pollProbability === null) {
      return adjustedBaseProbability
    }
    return adjustedBaseProbability * item.ensembleWeight + ((1 - item.ensembleWeight) * item.pollProbability)
  },
  getProbabilityDelta (final, original) {
    let value = null
    if (Math.abs(final - original) < 0.01) {
      value = 0
    } else if (this.mode === 'dem') {
      value = (Math.round((final - original) * 1e4) / 1e4) * -1
    } else {
      value = Math.round((final - original) * 1e4) / 1e4
    }
    let color = null
    if (value > 0) {
      color = '#57AEA6'
    } else if (value < 0) {
      color = '#FA3138'
    } else {
      color = 'rgba(0,0,0,.87)'
    }
    return {
      color,
      value
    }
  },
  getChange (item, prop) {
    const original = this.default.find(e => e.race === item.race)
    let color = null
    let negative = false
    if (item[prop] - original[prop] > 0) {
      color = '#57AEA6'
    } else if (item[prop] - original[prop] < 0) {
      negative = true
      color = '#FA3138'
    } else {
      color = 'rgba(0,0,0,.87)'
    }
    return {
      color,
      negative,
      absValue: Math.abs(item[prop] - original[prop]),
      value: item[prop] - original[prop]
    }
  },
  getRaceName (race) {
    let str = race.replace('2018', '')
    if (this.chamber === 'house') {
      let cd = str.slice(-2)
      str = str.substring(0, str.length - 2)
      return str + ' ' + cd
    } else {
      let special = str.slice(-2)
      special === '02' ? special = '(Special)' : special = ''
      str = str.substring(0, str.length - 2)
      return str + ' ' + special
    }
  },
  calculateItem (item) {
    item.originalProbability = Math.round(item.originalProbability * 1e5) / 1e5
    const adjustedBaseProbability = this.getBaseProbability(item)
    const finalProbability = Math.round(this.getFinalProbability(item, adjustedBaseProbability) * 1e5) / 1e5
    const gopChange = this.getChange(item, 'gopExpenditures')
    const demChange = this.getChange(item, 'demExpenditures')
    return {
      ...item,
      name: this.getRaceName(item.race),
      adjustedBaseProbability,
      finalProbability,
      probabilityDelta: this.getProbabilityDelta(finalProbability, item.originalProbability),
      gopChange,
      demChange,
      prediction: Utils.getColorScale(item.originalProbability * 100)
    }
  }
}

export default {
  computed,
  methods,
  data () {
    return {
      initialMaxSpend: {
        house: { dem: 325290696, gop: 255887142 },
        senate: { dem: 783714990, gop: 510862916 }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/calculator.js